import type { ColumnState } from 'ag-grid-community'

import { CANONICAL_PARAM_KEYS } from '@/components/lemmala-design-system/table/consts'
import type { FilterModel, SortModelEntry } from '@/components/lemmala-design-system/table/types'

// Parse unified FILTER param (JSON) + fold legacy RISK/STATUS into filter model
export const urlToFilterModel = (
  params: Record<string, string | undefined>,
  keys: { filter: string; legacyRisk?: string; legacyStatus?: string } = {
    filter: CANONICAL_PARAM_KEYS.filter,
    legacyRisk: CANONICAL_PARAM_KEYS.legacyRisk,
    legacyStatus: CANONICAL_PARAM_KEYS.legacyStatus,
  },
): FilterModel => {
  const model: FilterModel = {}

  const raw = params[keys.filter]
  if (raw) {
    try {
      const decoded = decodeURIComponent(raw)
      const parsed = JSON.parse(decoded) as FilterModel
      Object.assign(model, parsed)
    } catch {
      // ignore malformed FILTER param
    }
  }

  // Fold legacy RISK/STATUS into unified FILTER
  if (keys.legacyRisk && params[keys.legacyRisk]) {
    const values = decodeURIComponent(params[keys.legacyRisk]!).split(',').filter(Boolean)
    if (values.length > 0) {
      model['risk'] = { filterType: 'set', values }
    }
  }
  if (keys.legacyStatus && params[keys.legacyStatus]) {
    const values = decodeURIComponent(params[keys.legacyStatus]!).split(',').filter(Boolean)
    if (values.length > 0) {
      model['status'] = { filterType: 'set', values }
    }
  }

  return model
}

// Produce URL updates for FILTER, ensuring legacy params are removed
export const getFilterUrlUpdates = (
  filterModel: FilterModel,
  keys: { filter: string; legacyRisk?: string; legacyStatus?: string } = {
    filter: CANONICAL_PARAM_KEYS.filter,
    legacyRisk: CANONICAL_PARAM_KEYS.legacyRisk,
    legacyStatus: CANONICAL_PARAM_KEYS.legacyStatus,
  },
): { update: Array<[string, string]>; remove: string[] } => {
  const update: Array<[string, string]> = []
  const remove: string[] = []

  const hasFilters = Object.keys(filterModel || {}).length > 0
  if (hasFilters) {
    const encoded = encodeURIComponent(JSON.stringify(filterModel))
    update.push([keys.filter, encoded])
  } else {
    remove.push(keys.filter)
  }

  if (keys.legacyRisk) remove.push(keys.legacyRisk)
  if (keys.legacyStatus) remove.push(keys.legacyStatus)

  return { update, remove }
}

export const sortModelToUrlParam = (sortModel: SortModelEntry[]): string | null => {
  if (!Array.isArray(sortModel) || sortModel.length === 0) return null
  const parts = sortModel.map((s) => `${encodeURIComponent(s.colId)}:${s.sort}`)
  return parts.join(',')
}

export const urlParamToSortModel = (raw: string | null | undefined): SortModelEntry[] => {
  if (!raw) return []
  const parts = raw.split(',').filter(Boolean)
  const result: SortModelEntry[] = []
  for (const p of parts) {
    const [col, dir] = p.split(':')
    if (!col || (dir !== 'asc' && dir !== 'desc')) continue
    result.push({ colId: decodeURIComponent(col), sort: dir })
  }
  return result
}
export const shouldRemoveFilterParam = (filterModel: FilterModel): boolean =>
  Object.keys(filterModel || {}).length === 0

/**
 * Serialize column state to URL parameter.
 * Visible column ids in order. Hidden columns are omitted.
 */
export const columnStateToUrlParam = (columnState: ColumnState[]): string | null => {
  if (!Array.isArray(columnState) || columnState.length === 0) return null

  // Get all visible columns in order
  const visibleColumns = columnState
    .filter((col) => col.colId && !col.hide)
    .map((col) => col.colId as string)

  // Encode as comma-separated list of column IDs
  // Hidden columns are simply not included
  if (visibleColumns.length === 0) return null

  return visibleColumns.join(',')
}

/**
 * Parse column state from URL parameter.
 * Ids in the list are visible, in that order; every other known id is hidden.
 */
export const urlParamToColumnState = (
  raw: string | null | undefined,
  allColumnIds: string[],
): Partial<ColumnState>[] | null => {
  if (!raw) return null

  const visibleColumnIds = raw.split(',').filter(Boolean).map(decodeURIComponent)

  if (visibleColumnIds.length === 0) return null

  // Create column state: visible columns in order, then hidden columns
  const state: Partial<ColumnState>[] = []

  // Add visible columns in the order specified
  visibleColumnIds.forEach((colId) => {
    if (allColumnIds.includes(colId)) {
      state.push({
        colId,
        hide: false,
        rowGroupIndex: null,
        pivotIndex: null,
        aggFunc: null,
      })
    }
  })

  // Add hidden columns
  allColumnIds.forEach((colId) => {
    if (!visibleColumnIds.includes(colId)) {
      state.push({
        colId,
        hide: true,
        rowGroupIndex: null,
        pivotIndex: null,
        aggFunc: null,
      })
    }
  })

  return state
}

/**
 * Convert a params record that may include undefined values into a clean
 * Record<string, string> by dropping undefined entries. This avoids unsafe casts.
 */
export const toDefinedParams = (
  params: Record<string, string | undefined>,
): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => typeof value === 'string'),
  ) as Record<string, string>
}
